import ks from "@shared/kalmstromShared4";
import { IListDefinition, IViewDefinition, IFieldDefinition, IAggregation } from "./DBInterfaces";
import { VIEW_TYPES, AGGREGATION_FUNCTIONS } from "./DBConstants";
import DBState from "./DBState";
import DBModalManager from "./DBModalManager";
import DBStringUtils from "./DBStringUtils";
import DBListManager from "./DBListManager";
import DBFieldManager from "./DBFieldManager";

export default class DBViewManager {

    public static GenerateViewId = (): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            return "view_" + (++DBState.FieldIdCounter);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.GenerateViewId");
            return "";
        }
    };

    public static OpenManageViewsModal = (ListId: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(ListId);
            if (list === null) { return; }
            DBState.ManageViewsListId = ListId;
            const titleEl: HTMLElement | null = document.getElementById("manageViewsModalTitle");
            if (titleEl !== null) { titleEl.textContent = "Manage Views: " + list.Title; }
            const addBtn: HTMLElement | null = document.getElementById("manageViewsAddBtn");
            if (addBtn !== null) { (addBtn as HTMLElement).dataset.listId = ListId; }
            DBViewManager.RenderManageViewsContent(list);
            DBModalManager.OpenModal("manageViewsModal");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.OpenManageViewsModal");
        }
    };

    public static RefreshManageViewsModal = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (DBState.ManageViewsListId === null) { return; }
            const list: IListDefinition | null = DBState.FindList(DBState.ManageViewsListId);
            if (list === null) { return; }
            DBViewManager.RenderManageViewsContent(list);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.RefreshManageViewsModal");
        }
    };

    private static RenderManageViewsContent = (List: IListDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const container: HTMLElement | null = document.getElementById("manageViewsContent");
            if (container === null) { return; }
            const views: IViewDefinition[] = List.Views || [];
            if (views.length === 0) {
                container.innerHTML = '<p style="color: var(--text-secondary); text-align: center; padding: 24px;">No views yet. Click "+ Add View" to create one.</p>';
                return;
            }
            let html: string = "";
            views.forEach((view: IViewDefinition) => { html += DBViewManager.BuildSingleViewItem(List, view); });
            container.innerHTML = html;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.RenderManageViewsContent");
        }
    };

    public static OpenViewEditor = (ListId: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(ListId);
            if (list === null) { return; }
            DBState.EditingViewListId = ListId;
            DBState.EditingViewId = null;
            DBViewManager.RenderViewEditorContent(list, null);
            const titleEl: HTMLElement | null = document.getElementById("viewEditorModalTitle");
            if (titleEl !== null) { titleEl.textContent = "Add View: " + list.Title; }
            DBModalManager.OpenModal("viewEditorModal");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.OpenViewEditor");
        }
    };

    public static EditView = (ListId: string, ViewId: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(ListId);
            if (list === null) { return; }
            const views: IViewDefinition[] = list.Views !== null && list.Views !== undefined ? list.Views : [];
            const FindById = (V: IViewDefinition): boolean => { return V.Id === ViewId; };
            const view: IViewDefinition | undefined = views.find(FindById);
            if (view === undefined) { return; }
            DBState.EditingViewListId = ListId;
            DBState.EditingViewId = ViewId;
            DBViewManager.RenderViewEditorContent(list, view);
            const titleEl: HTMLElement | null = document.getElementById("viewEditorModalTitle");
            if (titleEl !== null) { titleEl.textContent = "Edit View: " + view.Title; }
            DBModalManager.OpenModal("viewEditorModal");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.EditView");
        }
    };

    public static RenderViewEditorContent = (List: IListDefinition, View: IViewDefinition | null): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const fields: [string, IFieldDefinition][] = Object.entries(List.Properties || {}) as [string, IFieldDefinition][];
            let html: string = "";
            html += DBViewManager.BuildViewEditorHeader();
            html += DBViewManager.BuildViewNameAndType(View);
            html += DBViewManager.BuildViewFieldsSelector(List, View, fields);
            html += DBViewManager.BuildViewSettings(View);
            html += DBViewManager.BuildViewAggregations(fields, View);
            html += DBViewManager.BuildViewFormatter(View);
            const contentEl: HTMLElement | null = document.getElementById("viewEditorContent");
            if (contentEl !== null) { contentEl.innerHTML = html; }
            DBViewManager.SetupViewFieldsInteraction();
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.RenderViewEditorContent");
        }
    };

    private static BuildViewEditorHeader = (): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            return '<div style="padding: 6px 10px; background: var(--bg-tertiary, #f5f5f5); border: 1px solid var(--border-color, #ddd); border-radius: 6px; margin-bottom: 12px; font-size: 11px; color: var(--text-secondary, #666);">'
                + '<span class="ps1-xml">PS1 + XML</span> = PowerShell &amp; PnP XML (not in Site Script) &nbsp;'
                + '<span class="ps1-only">PS1</span> = PowerShell only'
                + '</div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.BuildViewEditorHeader");
            return "";
        }
    };

    private static BuildViewNameAndType = (View: IViewDefinition | null): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const ps1XmlBadge: string = '<span class="ps1-xml" title="Included in PowerShell and PnP XML output, not supported by Site Script">PS1 + XML</span>';
            const titleValue: string = View !== null ? DBStringUtils.EscapeHtml(View.Title) : "";
            const isDefaultChecked: string = View !== null && View.IsDefault ? " checked" : "";
            const viewType: string = View !== null ? View.ViewType : "";

            const htmlSelected: string = !View || viewType === "HTML" ? " selected" : "";
            const gridSelected: string = viewType === "GRID" ? " selected" : "";
            const calendarSelected: string = viewType === "CALENDAR" ? " selected" : "";

            return '<div class="form-group">'
                + '<label class="form-label">View Name</label>'
                + '<input type="text" class="form-input" id="veTitle" value="' + titleValue + '" placeholder="e.g., All Items">'
                + '</div>'
                + '<div style="display: flex; gap: 16px;">'
                + '<div class="form-group" style="flex: 1;">'
                + '<label class="form-label">Default View</label>'
                + '<label style="font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 8px;">'
                + '<input type="checkbox" id="veIsDefault"' + isDefaultChecked + '> Set as default view'
                + '</label></div>'
                + '<div class="form-group" style="flex: 1;">'
                + '<label class="form-label">View Type ' + ps1XmlBadge + '</label>'
                + '<select class="form-select" id="veViewType" style="width: 100%; padding: 6px 8px; border: 1px solid var(--border-color); border-radius: 4px; background: var(--bg-primary); color: var(--text-primary);">'
                + '<option value="HTML"' + htmlSelected + '>Standard</option>'
                + '<option value="GRID"' + gridSelected + '>Datasheet / Grid</option>'
                + '<option value="CALENDAR"' + calendarSelected + '>Calendar</option>'
                + '</select></div></div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.BuildViewNameAndType");
            return "";
        }
    };

    private static BuildViewFieldsSelector = (List: IListDefinition, View: IViewDefinition | null, Fields: [string, IFieldDefinition][]): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const selectedFields: string[] = View !== null ? View.ViewFields.slice() : DBViewManager.GetAllFieldNames(Fields);
            const orderedFields: { Name: string; Field: IFieldDefinition; Checked: boolean }[] = DBViewManager.BuildOrderedFields(Fields, selectedFields);

            let html: string = '<div class="form-group">'
                + '<label class="form-label">View Fields (check to include, use arrows to reorder)</label>'
                + '<div class="view-fields-selector" id="veFieldsContainer">';

            const RenderFieldItem = (Item: { Name: string; Field: IFieldDefinition; Checked: boolean }, Index: number): void => {
                html += DBViewManager.BuildSingleFieldItem(Item, Index);
            };
            orderedFields.forEach(RenderFieldItem);

            html += '</div></div>';
            return html;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.BuildViewFieldsSelector");
            return "";
        }
    };

    private static GetAllFieldNames = (Fields: [string, IFieldDefinition][]): string[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const result: string[] = [];
            const AddName = (Entry: [string, IFieldDefinition]): void => {
                result.push(Entry[0]);
            };
            Fields.forEach(AddName);
            return result;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.GetAllFieldNames");
            return [];
        }
    };

    private static BuildOrderedFields = (Fields: [string, IFieldDefinition][], SelectedFields: string[]): { Name: string; Field: IFieldDefinition; Checked: boolean }[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const ordered: { Name: string; Field: IFieldDefinition; Checked: boolean }[] = [];

            const AddSelected = (Name: string): void => {
                const FindByName = (Entry: [string, IFieldDefinition]): boolean => { return Entry[0] === Name; };
                const entry: [string, IFieldDefinition] | undefined = Fields.find(FindByName);
                if (entry !== undefined) {
                    ordered.push({ Name: entry[0], Field: entry[1], Checked: true });
                }
            };
            SelectedFields.forEach(AddSelected);

            const AddUnselected = (Entry: [string, IFieldDefinition]): void => {
                const IsIncluded: boolean = SelectedFields.indexOf(Entry[0]) !== -1;
                if (!IsIncluded) {
                    ordered.push({ Name: Entry[0], Field: Entry[1], Checked: false });
                }
            };
            Fields.forEach(AddUnselected);

            return ordered;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.BuildOrderedFields");
            return [];
        }
    };

    private static BuildSingleFieldItem = (Item: { Name: string; Field: IFieldDefinition; Checked: boolean }, Index: number): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const selectedClass: string = Item.Checked ? " selected" : "";
            const checkedAttr: string = Item.Checked ? " checked" : "";
            const escapedName: string = DBStringUtils.EscapeHtml(Item.Name);
            const typeLabel: string = DBStringUtils.EscapeHtml(Item.Field.Format || Item.Field.Type || "");
            const displayTitle: string = Item.Field.Title !== null && Item.Field.Title !== undefined && Item.Field.Title !== "" ? Item.Field.Title : Item.Name;

            return '<div class="view-field-item' + selectedClass + '" data-field-name="' + escapedName + '">'
                + '<input type="checkbox"' + checkedAttr + ' data-field-name="' + escapedName + '">'
                + '<span class="field-name">' + DBStringUtils.EscapeHtml(displayTitle) + '</span>'
                + '<span class="field-type">' + typeLabel + '</span>'
                + '<div class="view-field-order-btns">'
                + '<button class="view-field-order-btn" data-action="move-view-field-up" data-index="' + Index + '" title="Move up">&#9650;</button>'
                + '<button class="view-field-order-btn" data-action="move-view-field-down" data-index="' + Index + '" title="Move down">&#9660;</button>'
                + '</div></div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.BuildSingleFieldItem");
            return "";
        }
    };

    private static BuildViewSettings = (View: IViewDefinition | null): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const ps1XmlBadge: string = '<span class="ps1-xml" title="Included in PowerShell and PnP XML output, not supported by Site Script">PS1 + XML</span>';
            const rowLimitValue: number = View !== null ? View.RowLimit : 30;
            const pagedChecked: string = View === null || View.Paged !== false ? " checked" : "";
            const queryValue: string = View !== null ? DBStringUtils.EscapeHtml(View.ViewQuery) : "";
            const hiddenChecked: string = View !== null && View.Hidden ? " checked" : "";
            const scopeValue: string = View !== null ? View.Scope : "";

            const scopeDefaultSelected: string = !View || scopeValue === "Default" ? " selected" : "";
            const scopeRecursiveSelected: string = scopeValue === "RecursiveAll" ? " selected" : "";
            const scopeFilesSelected: string = scopeValue === "FilesOnly" ? " selected" : "";

            return DBViewManager.BuildRowLimitAndPaged(rowLimitValue, pagedChecked)
                + DBViewManager.BuildViewQuery(queryValue)
                + DBViewManager.BuildHiddenAndScope(ps1XmlBadge, hiddenChecked, scopeDefaultSelected, scopeRecursiveSelected, scopeFilesSelected);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.BuildViewSettings");
            return "";
        }
    };

    private static BuildRowLimitAndPaged = (RowLimitValue: number, PagedChecked: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            return '<div style="display: flex; gap: 16px;">'
                + '<div class="form-group" style="flex: 1;">'
                + '<label class="form-label">Row Limit</label>'
                + '<input type="number" class="form-input" id="veRowLimit" value="' + RowLimitValue + '" min="1" max="5000" style="width: 100%;">'
                + '</div>'
                + '<div class="form-group" style="flex: 1;">'
                + '<label class="form-label">Pagination</label>'
                + '<label style="font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 8px;">'
                + '<input type="checkbox" id="vePaged"' + PagedChecked + '> Enable paging'
                + '</label></div></div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.BuildRowLimitAndPaged");
            return "";
        }
    };

    private static BuildViewQuery = (QueryValue: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            return '<div class="form-group">'
                + '<label class="form-label">View Query (CAML XML)</label>'
                + '<textarea class="form-textarea" id="veViewQuery" rows="3" placeholder=\'<Where><Eq><FieldRef Name="Status"/><Value Type="Choice">Active</Value></Eq></Where>\' style="font-family: \'Courier New\', monospace; font-size: 11px; width: 100%; padding: 8px; border: 1px solid var(--border-color); border-radius: 4px; background: var(--bg-primary); color: var(--text-primary); resize: vertical;">'
                + QueryValue
                + '</textarea>'
                + '<div style="font-size: 11px; color: var(--text-secondary); margin-top: 2px;">Optional CAML query for filtering and sorting</div>'
                + '</div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.BuildViewQuery");
            return "";
        }
    };

    private static BuildHiddenAndScope = (Ps1XmlBadge: string, HiddenChecked: string, ScopeDefaultSelected: string, ScopeRecursiveSelected: string, ScopeFilesSelected: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            return '<div style="display: flex; gap: 16px;">'
                + '<div class="form-group" style="flex: 1;">'
                + '<label class="form-label">Hidden ' + Ps1XmlBadge + '</label>'
                + '<label style="font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 8px;">'
                + '<input type="checkbox" id="veHidden"' + HiddenChecked + '> Hidden from view selector'
                + '</label></div>'
                + '<div class="form-group" style="flex: 1;">'
                + '<label class="form-label">Scope</label>'
                + '<select class="form-select" id="veScope" style="width: 100%; padding: 6px 8px; border: 1px solid var(--border-color); border-radius: 4px; background: var(--bg-primary); color: var(--text-primary);">'
                + '<option value="Default"' + ScopeDefaultSelected + '>Default</option>'
                + '<option value="RecursiveAll"' + ScopeRecursiveSelected + '>Recursive All (include subfolders)</option>'
                + '<option value="FilesOnly"' + ScopeFilesSelected + '>Files Only</option>'
                + '</select></div></div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.BuildHiddenAndScope");
            return "";
        }
    };

    private static BuildViewAggregations = (Fields: [string, IFieldDefinition][], View: IViewDefinition | null): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const ps1XmlBadge: string = '<span class="ps1-xml" title="Included in PowerShell and PnP XML output, not supported by Site Script">PS1 + XML</span>';
            const aggregations: IAggregation[] = View !== null && View.Aggregations !== null && View.Aggregations !== undefined ? View.Aggregations : [];

            let html: string = '<div class="form-group">'
                + '<label class="form-label">Aggregations (Totals) ' + ps1XmlBadge + '</label>'
                + '<div id="veAggregationsContainer">';

            const RenderRow = (Agg: IAggregation, Index: number): void => {
                html += DBViewManager.RenderAggregationRow(Fields, Agg, Index);
            };
            aggregations.forEach(RenderRow);

            html += '</div>'
                + '<button style="padding: 4px 10px; border: 1px dashed var(--border-color); border-radius: 4px; background: transparent; color: var(--text-secondary); font-size: 11px; cursor: pointer; margin-top: 4px;" data-action="add-view-aggregation">+ Add Aggregation</button>'
                + '</div>';
            return html;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.BuildViewAggregations");
            return "";
        }
    };

    private static BuildViewFormatter = (View: IViewDefinition | null): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const ps1Badge: string = '<span class="ps1-only" title="Only included in PowerShell output">PS1</span>';
            const formatterValue: string = View !== null ? DBStringUtils.EscapeHtml(View.CustomFormatter) : "";

            return '<div class="form-group">'
                + '<label class="form-label">View Formatting (JSON) ' + ps1Badge + '</label>'
                + '<textarea class="form-textarea" id="veCustomFormatter" rows="3" placeholder=\'{"$schema":"..."}\' style="font-family: \'Courier New\', monospace; font-size: 11px; width: 100%; padding: 8px; border: 1px solid var(--border-color); border-radius: 4px; background: var(--bg-primary); color: var(--text-primary); resize: vertical;">'
                + formatterValue
                + '</textarea>'
                + '<div style="font-size: 11px; color: var(--text-secondary); margin-top: 2px;">JSON view formatting for modern experience</div>'
                + '</div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.BuildViewFormatter");
            return "";
        }
    };

    public static RenderAggregationRow = (Fields: [string, IFieldDefinition][], Agg: IAggregation | null, Index: number): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let html: string = '<div class="aggregation-row" data-agg-index="' + Index + '">'
                + '<select class="agg-field" data-agg-index="' + Index + '">'
                + '<option value="">-- Select Field --</option>';

            const RenderFieldOption = (Entry: [string, IFieldDefinition]): void => {
                const fieldName: string = Entry[0];
                const field: IFieldDefinition = Entry[1];
                const selectedAttr: string = Agg !== null && Agg.FieldName === fieldName ? " selected" : "";
                const displayTitle: string = field.Title !== null && field.Title !== undefined && field.Title !== "" ? field.Title : fieldName;
                html += '<option value="' + DBStringUtils.EscapeHtml(fieldName) + '"' + selectedAttr + '>' + DBStringUtils.EscapeHtml(displayTitle) + '</option>';
            };
            Fields.forEach(RenderFieldOption);

            html += '</select><select class="agg-function" data-agg-index="' + Index + '">';

            const RenderFuncOption = (Fn: string): void => {
                const selectedAttr: string = Agg !== null && Agg.Function === Fn ? " selected" : "";
                html += '<option value="' + Fn + '"' + selectedAttr + '>' + Fn + '</option>';
            };
            AGGREGATION_FUNCTIONS.forEach(RenderFuncOption);

            html += '</select>'
                + '<button class="aggregation-remove-btn" data-action="remove-view-aggregation" data-agg-index="' + Index + '" title="Remove">&times;</button>'
                + '</div>';
            return html;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.RenderAggregationRow");
            return "";
        }
    };

    public static SetupViewFieldsInteraction = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const container: HTMLElement | null = document.getElementById("veFieldsContainer");
            if (container === null) { return; }

            const HandleChange = (E: Event): void => {
                const target: HTMLElement = E.target as HTMLElement;
                if ((target as HTMLInputElement).type === "checkbox") {
                    const item: HTMLElement | null = target.closest(".view-field-item");
                    if (item !== null) {
                        item.classList.toggle("selected", (target as HTMLInputElement).checked);
                    }
                }
            };
            container.addEventListener("change", HandleChange);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.SetupViewFieldsInteraction");
        }
    };

    public static MoveViewFieldUp = (Index: number): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const container: HTMLElement | null = document.getElementById("veFieldsContainer");
            if (container === null) { return; }
            const items: Element[] = Array.from(container.children);
            if (Index <= 0 || Index >= items.length) { return; }
            container.insertBefore(items[Index], items[Index - 1]);
            DBViewManager.UpdateViewFieldOrderButtons();
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.MoveViewFieldUp");
        }
    };

    public static MoveViewFieldDown = (Index: number): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const container: HTMLElement | null = document.getElementById("veFieldsContainer");
            if (container === null) { return; }
            const items: Element[] = Array.from(container.children);
            if (Index < 0 || Index >= items.length - 1) { return; }
            container.insertBefore(items[Index + 1], items[Index]);
            DBViewManager.UpdateViewFieldOrderButtons();
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.MoveViewFieldDown");
        }
    };

    public static UpdateViewFieldOrderButtons = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const container: HTMLElement | null = document.getElementById("veFieldsContainer");
            if (container === null) { return; }

            const UpdateItem = (Item: Element, Index: number): void => {
                const upBtn: HTMLElement | null = Item.querySelector('[data-action="move-view-field-up"]') as HTMLElement;
                const downBtn: HTMLElement | null = Item.querySelector('[data-action="move-view-field-down"]') as HTMLElement;
                if (upBtn !== null) { upBtn.dataset.index = String(Index); }
                if (downBtn !== null) { downBtn.dataset.index = String(Index); }
            };
            Array.from(container.children).forEach(UpdateItem);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.UpdateViewFieldOrderButtons");
        }
    };

    public static AddViewAggregation = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(DBState.EditingViewListId || "");
            if (list === null) { return; }
            const container: HTMLElement | null = document.getElementById("veAggregationsContainer");
            if (container === null) { return; }
            const fields: [string, IFieldDefinition][] = Object.entries(list.Properties || {}) as [string, IFieldDefinition][];
            const index: number = container.children.length;
            container.insertAdjacentHTML("beforeend", DBViewManager.RenderAggregationRow(fields, null, index));
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.AddViewAggregation");
        }
    };

    public static RemoveViewAggregation = (Index: number): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const container: HTMLElement | null = document.getElementById("veAggregationsContainer");
            if (container === null) { return; }
            const row: Element | null = container.querySelector('[data-agg-index="' + Index + '"]');
            if (row !== null) { row.remove(); }

            const ReIndex = (RowEl: Element, NewIndex: number): void => {
                (RowEl as HTMLElement).dataset.aggIndex = String(NewIndex);
                const aggField: HTMLElement | null = RowEl.querySelector(".agg-field") as HTMLElement;
                const aggFunc: HTMLElement | null = RowEl.querySelector(".agg-function") as HTMLElement;
                const rmBtn: HTMLElement | null = RowEl.querySelector('[data-action="remove-view-aggregation"]') as HTMLElement;
                if (aggField !== null) { aggField.dataset.aggIndex = String(NewIndex); }
                if (aggFunc !== null) { aggFunc.dataset.aggIndex = String(NewIndex); }
                if (rmBtn !== null) { rmBtn.dataset.aggIndex = String(NewIndex); }
            };
            Array.from(container.children).forEach(ReIndex);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.RemoveViewAggregation");
        }
    };

    public static SaveView = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(DBState.EditingViewListId || "");
            if (list === null) { return; }

            const titleInput: HTMLInputElement | null = document.getElementById("veTitle") as HTMLInputElement;
            const title: string = titleInput !== null ? titleInput.value.trim() : "";
            if (!title) {
                DBModalManager.ShowToast("View name is required", "error");
                return;
            }

            const viewFields: string[] = DBViewManager.CollectViewFields();
            if (viewFields.length === 0) {
                DBModalManager.ShowToast("At least one field must be selected", "error");
                return;
            }

            const aggregations: IAggregation[] = DBViewManager.CollectAggregations();
            const viewData: IViewDefinition = DBViewManager.BuildViewData(title, viewFields, aggregations);

            DBViewManager.ApplyViewToList(list, viewData);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.SaveView");
        }
    };

    private static CollectViewFields = (): string[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const container: HTMLElement | null = document.getElementById("veFieldsContainer");
            if (container === null) { return []; }
            const viewFields: string[] = [];

            const CheckItem = (Item: Element): void => {
                const checkbox: HTMLInputElement | null = Item.querySelector('input[type="checkbox"]') as HTMLInputElement;
                if (checkbox !== null && checkbox.checked) {
                    const fieldName: string | undefined = (Item as HTMLElement).dataset.fieldName;
                    if (fieldName !== undefined) { viewFields.push(fieldName); }
                }
            };
            Array.from(container.children).forEach(CheckItem);

            return viewFields;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.CollectViewFields");
            return [];
        }
    };

    private static CollectAggregations = (): IAggregation[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const aggContainer: HTMLElement | null = document.getElementById("veAggregationsContainer");
            if (aggContainer === null) { return []; }
            const aggregations: IAggregation[] = [];

            const CheckRow = (Row: Element): void => {
                const fieldSelect: HTMLSelectElement | null = Row.querySelector(".agg-field") as HTMLSelectElement;
                const funcSelect: HTMLSelectElement | null = Row.querySelector(".agg-function") as HTMLSelectElement;
                const fieldValue: string = fieldSelect !== null ? fieldSelect.value : "";
                const funcValue: string = funcSelect !== null ? funcSelect.value : "";
                if (fieldValue && funcValue) {
                    aggregations.push({ FieldName: fieldValue, Function: funcValue });
                }
            };
            Array.from(aggContainer.children).forEach(CheckRow);

            return aggregations;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.CollectAggregations");
            return [];
        }
    };

    private static BuildViewData = (Title: string, ViewFields: string[], Aggregations: IAggregation[]): IViewDefinition => {
        if (ks.kErrorMode === true) { return DBViewManager.CreateEmptyView(); }
        try {
            const isDefaultEl: HTMLInputElement | null = document.getElementById("veIsDefault") as HTMLInputElement;
            const viewTypeEl: HTMLSelectElement | null = document.getElementById("veViewType") as HTMLSelectElement;
            const viewQueryEl: HTMLTextAreaElement | null = document.getElementById("veViewQuery") as HTMLTextAreaElement;
            const rowLimitEl: HTMLInputElement | null = document.getElementById("veRowLimit") as HTMLInputElement;
            const pagedEl: HTMLInputElement | null = document.getElementById("vePaged") as HTMLInputElement;
            const hiddenEl: HTMLInputElement | null = document.getElementById("veHidden") as HTMLInputElement;
            const scopeEl: HTMLSelectElement | null = document.getElementById("veScope") as HTMLSelectElement;
            const formatterEl: HTMLTextAreaElement | null = document.getElementById("veCustomFormatter") as HTMLTextAreaElement;

            return {
                Id: "",
                Title: Title,
                IsDefault: isDefaultEl !== null ? isDefaultEl.checked : false,
                ViewType: viewTypeEl !== null ? viewTypeEl.value : "HTML",
                ViewFields: ViewFields,
                ViewQuery: viewQueryEl !== null ? viewQueryEl.value.trim() : "",
                RowLimit: rowLimitEl !== null ? (parseInt(rowLimitEl.value) || 30) : 30,
                Paged: pagedEl !== null ? pagedEl.checked : true,
                Hidden: hiddenEl !== null ? hiddenEl.checked : false,
                Scope: scopeEl !== null ? scopeEl.value : "Default",
                Aggregations: Aggregations,
                CustomFormatter: formatterEl !== null ? formatterEl.value.trim() : ""
            };
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.BuildViewData");
            return DBViewManager.CreateEmptyView();
        }
    };

    private static CreateEmptyView = (): IViewDefinition => {
        if (ks.kErrorMode === true) { return null as unknown as IViewDefinition; }
        try {
            return {
                Id: "",
                Title: "",
                IsDefault: false,
                ViewType: "HTML",
                ViewFields: [],
                ViewQuery: "",
                RowLimit: 30,
                Paged: true,
                Hidden: false,
                Scope: "Default",
                Aggregations: [],
                CustomFormatter: ""
            };
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.CreateEmptyView");
            return null as unknown as IViewDefinition;
        }
    };

    private static ApplyViewToList = (List: IListDefinition, ViewData: IViewDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (List.Views === null || List.Views === undefined) { List.Views = []; }

            if (ViewData.IsDefault) {
                const ClearDefault = (V: IViewDefinition): void => { V.IsDefault = false; };
                List.Views.forEach(ClearDefault);
            }

            const isEditing: boolean = DBState.EditingViewId !== null && DBState.EditingViewId !== "";

            if (isEditing) {
                DBViewManager.UpdateExistingView(List, ViewData);
            } else {
                ViewData.Id = DBViewManager.GenerateViewId();
                List.Views.push(ViewData);
            }

            DBState.SaveState();
            DBListManager.RenderLists();
            DBModalManager.CloseModal("viewEditorModal");
            DBViewManager.RefreshManageViewsModal();
            DBModalManager.ShowToast(isEditing ? "View updated" : "View added", "success");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.ApplyViewToList");
        }
    };

    private static UpdateExistingView = (List: IListDefinition, ViewData: IViewDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const FindById = (V: IViewDefinition): boolean => { return V.Id === DBState.EditingViewId; };
            const idx: number = List.Views.findIndex(FindById);
            if (idx >= 0) {
                ViewData.Id = DBState.EditingViewId || "";
                List.Views[idx] = ViewData;
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.UpdateExistingView");
        }
    };

    public static DeleteView = (ListId: string, ViewId: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(ListId);
            if (list === null) { return; }
            const views: IViewDefinition[] = list.Views !== null && list.Views !== undefined ? list.Views : [];
            const FindById = (V: IViewDefinition): boolean => { return V.Id === ViewId; };
            const view: IViewDefinition | undefined = views.find(FindById);
            if (view === undefined) { return; }

            if (confirm('Delete the view "' + view.Title + '"?')) {
                const KeepOthers = (V: IViewDefinition): boolean => { return V.Id !== ViewId; };
                list.Views = list.Views.filter(KeepOthers);
                DBState.SaveState();
                DBListManager.RenderLists();
                DBViewManager.RefreshManageViewsModal();
                DBModalManager.ShowToast("View deleted", "success");
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.DeleteView");
        }
    };

    public static RenderViewsOnCard = (List: IListDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const cardBody: Element | null = document.querySelector("#card_" + List.Id + " .list-body");
            if (cardBody === null) { return; }

            const existing: Element | null = cardBody.querySelector(".views-section");
            if (existing !== null) { existing.remove(); }

            const views: IViewDefinition[] = List.Views !== null && List.Views !== undefined ? List.Views : [];
            const section: HTMLDivElement = document.createElement("div");
            section.className = "views-section";

            if (views.length === 0 && !List.Collapsed) {
                section.innerHTML = '<button class="add-view-btn" data-action="add-view" data-list-id="' + DBStringUtils.EscapeHtml(List.Id) + '">+ Add View</button>';
                cardBody.appendChild(section);
                return;
            }

            if (List.Collapsed || views.length === 0) { return; }

            section.innerHTML = DBViewManager.BuildViewsCardContent(List, views);
            cardBody.appendChild(section);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.RenderViewsOnCard");
        }
    };

    private static BuildViewsCardContent = (List: IListDefinition, Views: IViewDefinition[]): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let html: string = '<div class="views-section-header">'
                + '<span class="views-section-title">Views</span>'
                + '<span class="views-section-count">' + Views.length + '</span>'
                + '</div>';

            const RenderViewItem = (View: IViewDefinition): void => {
                html += DBViewManager.BuildSingleViewItem(List, View);
            };
            Views.forEach(RenderViewItem);

            html += '<button class="add-view-btn" data-action="add-view" data-list-id="' + DBStringUtils.EscapeHtml(List.Id) + '">+ Add View</button>';
            return html;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.BuildViewsCardContent");
            return "";
        }
    };

    private static BuildSingleViewItem = (List: IListDefinition, View: IViewDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const viewTypeDef: { Label: string; Icon: string; Value: number } | undefined = VIEW_TYPES[View.ViewType];
            const typeIcon: string = viewTypeDef !== undefined ? viewTypeDef.Icon : "\uD83D\uDCCB";
            const typeLabel: string = viewTypeDef !== undefined ? viewTypeDef.Label : "Standard";
            const typeBadgeClass: string = View.ViewType === "GRID" ? "grid" : (View.ViewType === "CALENDAR" ? "calendar" : "");
            const fieldCount: number = View.ViewFields !== null && View.ViewFields !== undefined ? View.ViewFields.length : 0;
            const rowLimit: number = View.RowLimit || 30;
            const escapedListId: string = DBStringUtils.EscapeHtml(List.Id);
            const escapedViewId: string = DBStringUtils.EscapeHtml(View.Id);
            const defaultBadge: string = View.IsDefault ? '<span class="view-default-badge">Default</span>' : "";
            const hiddenBadge: string = View.Hidden ? '<span class="view-hidden-badge">Hidden</span>' : "";

            return '<div class="view-item" data-view-id="' + escapedViewId + '" data-list-id="' + escapedListId + '">'
                + '<span class="view-item-icon">' + typeIcon + '</span>'
                + '<div class="view-item-info">'
                + '<div class="view-item-title">' + DBStringUtils.EscapeHtml(View.Title) + '</div>'
                + '<div class="view-item-meta">' + fieldCount + ' columns, ' + rowLimit + ' rows</div>'
                + '</div>'
                + defaultBadge
                + hiddenBadge
                + '<span class="view-type-badge ' + typeBadgeClass + '">' + typeLabel + '</span>'
                + '<div class="view-actions">'
                + '<button class="view-action-btn edit" data-action="edit-view" data-list-id="' + escapedListId + '" data-view-id="' + escapedViewId + '" title="Edit view">&#9998;</button>'
                + '<button class="view-action-btn" data-action="delete-view" data-list-id="' + escapedListId + '" data-view-id="' + escapedViewId + '" title="Delete view">&times;</button>'
                + '</div></div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.BuildSingleViewItem");
            return "";
        }
    };

    public static ParseAggregationsXml = (Xml: string): IAggregation[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            if (!Xml) { return []; }
            const aggregations: IAggregation[] = [];
            const regex: RegExp = /FieldRef\s+Name="([^"]+)"\s+Type="([^"]+)"/g;
            let match: RegExpExecArray | null = regex.exec(Xml);

            while (match !== null) {
                aggregations.push({ FieldName: match[1], Function: match[2].toUpperCase() });
                match = regex.exec(Xml);
            }

            return aggregations;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBViewManager.ParseAggregationsXml");
            return [];
        }
    };
}
